/**
 * SocialCompassModule — the client face of the Social Pulse & Social Compass (P6).
 *
 * Three surfaces, all pure mirrors of authoritative rows:
 *
 *   • Pulse HUD (always-on, bottom-centre) — the local player's four needs as
 *     decaying gauges, plus a hint when the current world is a venue that lifts
 *     one of them. The client periodically nudges the server to advance its own
 *     Pulse (`decayAndSatisfyPulse`); it never computes a gauge value itself.
 *   • Compass panel (toggle 'C') — pick a Want + Fear and publish them
 *     (`rotateCompass` / `setCompassPublic`), opt each need into visibility
 *     (`setPulseVisibility`), and claim the flat daily SOV faucet
 *     (`claimDailyPulseReward`).
 *   • Overhead labels (full-screen overlay) — every public Compass within 10 m
 *     floats a Want/Fear tag over its avatar. A compatible Want highlights gold
 *     and offers a Wave button (`sendWave`); the server re-checks range + public
 *     + compatibility and is the sole authority on whether the Wave lands.
 *
 * HARD INVARIANT mirrored from the server: nothing here is punitive. An empty
 * gauge renders calmly (it is an invitation, not a warning), no control is
 * locked behind a need, and the daily reward button never scolds a missed day.
 * Privacy is opt-in: needs and the Compass are hidden until the player publishes
 * them, and strangers are only rendered when their Compass is explicitly public.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { sfx } from '../lib/sfx.js';
import { makeSystem, Phase } from '../engine-compat.js';
import {
  NEED,
  NEEDS,
  WANTS,
  WANT_GROUP,
  WAVE_RANGE_M,
  PULSE_MAX,
  wantsCompatible,
  type NeedName,
} from '@shared/constants.js';

/** How often (seconds) the client asks the server to advance the local Pulse. */
const PULSE_DRIVE_INTERVAL_S = 5;
/** Per-need display metadata (label + accent colour for the bar fill). */
const NEED_META: Readonly<Record<NeedName, { label: string; color: string }>> = {
  social: { label: 'Social', color: '#4fc3f7' },
  fun: { label: 'Fun', color: '#e3b341' },
  creativity: { label: 'Creativity', color: '#bb86fc' },
  rest: { label: 'Rest', color: '#56d364' },
};

/** Humanize a Want key for display: `deep_conversation` → `deep conversation`. */
function humanizeWant(want: string): string {
  return want.replace(/_/g, ' ');
}

function shortHex(hex: string): string {
  return hex.length > 12 ? `${hex.slice(0, 6)}\u2026${hex.slice(-4)}` : hex;
}

export class SocialCompassModule implements IModule {
  readonly name = 'SocialCompass';

  private app!: App;
  private canvas!: HTMLCanvasElement;

  // Pulse HUD.
  private pulseEl!: HTMLElement;
  private pulseFills = new Map<NeedName, HTMLElement>();
  private pulseVals = new Map<NeedName, HTMLElement>();
  private pulseHint!: HTMLElement;

  // Compass panel.
  private panel!: HTMLElement;
  private wantSel!: HTMLSelectElement;
  private fearInput!: HTMLInputElement;
  private rotateBtn!: HTMLButtonElement;
  private publicChk!: HTMLInputElement;
  private rewardBtn!: HTMLButtonElement;
  private noteEl!: HTMLElement;
  private visChks = new Map<NeedName, HTMLInputElement>();
  private open = false;

  // Overhead Want/Fear labels.
  private labelsRoot!: HTMLElement;
  private labelEls = new Map<string, HTMLElement>();

  // Projection scratch — reused every frame, no per-frame allocation.
  private readonly tmpWorld = new THREE.Vector3();
  private readonly tmpProj = new THREE.Vector3();
  private readonly tmpCamPos = new THREE.Vector3();
  private readonly _seenScratch = new Set<string>();
  private readonly tmpCamDir = new THREE.Vector3();
  private readonly tmpToPoint = new THREE.Vector3();

  /** Seconds accumulated toward the next Pulse drive. */
  private pulseDriveAccum = 0;

  init(app: App): void {
    this.app = app;
    this.canvas = app.renderer.native.domElement as HTMLCanvasElement;

    this.pulseEl = document.getElementById('pulse') as HTMLElement;
    this.panel = document.getElementById('compass') as HTMLElement;
    this.labelsRoot = document.getElementById('social-labels') as HTMLElement;

    this.buildPulseHud();
    this.buildPanel();

    // The Pulse HUD reveals with the account; the Compass panel is opened on 'C'.
    bus.on('atlas:account-ready', () => {
      this.pulseEl.classList.add('show');
      this.driveLocalPulse();
      this.renderPulse();
      this.renderPanel();
    });
    bus.on('atlas:pulse-updated', () => this.renderPulse());
    bus.on('atlas:compass-updated', () => this.renderPanel());
    // A venue may lift a different need in the new world — refresh the hint.
    bus.on('atlas:world-entered', () => this.renderPulse());

    window.addEventListener('keydown', (e) => {
      const typing =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement;
      if (e.key === 'Escape' && this.open) {
        this.close();
      } else if ((e.key === 'c' || e.key === 'C') && !typing && atlas.hasAccount) {
        e.preventDefault();
        this.toggle();
      }
    });

    // UI-phase tick: drives the local Pulse on a timer and projects the overhead
    // Want/Fear labels every frame (mirrors ForgeCanvas's cursor projection).
    app.world.addSystem(
      makeSystem('SocialCompass', Phase.UI, 26, (dt: number) => this.tick(dt)),
    );
  }

  // ─── Pulse HUD ────────────────────────────────────────────────────────────────

  private buildPulseHud(): void {
    this.pulseEl.innerHTML = '';
    const bars = document.createElement('div');
    bars.className = 'pulse-bars';
    for (const need of NEEDS) {
      const meta = NEED_META[need];
      const row = document.createElement('div');
      row.className = 'pulse-bar';
      const name = document.createElement('span');
      name.className = 'pulse-name';
      name.textContent = meta.label;
      const track = document.createElement('div');
      track.className = 'pulse-track';
      const fill = document.createElement('div');
      fill.className = 'pulse-fill';
      fill.style.background = meta.color;
      track.appendChild(fill);
      const val = document.createElement('span');
      val.className = 'pulse-val';
      val.textContent = '–';
      row.append(name, track, val);
      bars.appendChild(row);
      this.pulseFills.set(need, fill);
      this.pulseVals.set(need, val);
    }
    this.pulseEl.appendChild(bars);
    this.pulseHint = document.createElement('div');
    this.pulseHint.className = 'pulse-hint';
    this.pulseEl.appendChild(this.pulseHint);
  }

  private renderPulse(): void {
    const p = atlas.localPulse;
    for (const need of NEEDS) {
      const fill = this.pulseFills.get(need)!;
      const val = this.pulseVals.get(need)!;
      const v = p ? Math.max(0, Math.min(PULSE_MAX, p[need])) : 0;
      fill.style.width = `${(v / PULSE_MAX) * 100}%`;
      val.textContent = p ? String(Math.round(v)) : '–';
    }

    // Venue hint: does the current world lift one of the needs?
    const worldId = atlas.currentWorldId;
    const sat = worldId !== null ? atlas.needSatisfiers.get(worldId.toString()) : undefined;
    if (sat && sat.ratePerMin > 0) {
      const meta = NEED_META[sat.need];
      this.pulseHint.innerHTML = `\u2728 This place lifts <b style="color:${meta.color}">${meta.label}</b> (+${sat.ratePerMin}/min)`;
      this.pulseHint.style.display = 'block';
    } else {
      this.pulseHint.style.display = 'none';
    }
  }

  /** Ask the server to advance the local player's Pulse to "now" (bootstraps it). */
  private driveLocalPulse(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const identity = conn.identity;
    if (!identity) return;
    // The server's decayAndSatisfyPulse decays/satisfies on whole-minute boundaries — it is
    // a NO-OP below 60 s since updatedAt. So the 5 s poll just wastes a round-trip most of
    // the time; gate it to ≤1/min (≈12× fewer reducer calls, identical behaviour: the
    // skipped calls would no-op server-side anyway). The first drive (localPulse null) fires.
    const p = atlas.localPulse;
    if (p && BigInt(Date.now()) * 1000n - p.updatedAt < 60_000_000n) return;
    void conn.reducers.decayAndSatisfyPulse({ identity }).catch(() => {
      /* Transient — the next timer tick retries; never surfaced to the player. */
    });
  }

  // ─── Compass panel ──────────────────────────────────────────────────────────────

  private buildPanel(): void {
    this.panel.innerHTML = '';

    const title = document.createElement('h2');
    title.textContent = 'Social Compass';
    this.panel.appendChild(title);

    const blurb = document.createElement('p');
    blurb.className = 'compass-blurb';
    blurb.textContent =
      'Share what you are looking for. Compatible strangers nearby light up gold and can break the ice with a Wave.';
    this.panel.appendChild(blurb);

    // Want picker, grouped by affinity.
    this.panel.appendChild(this.fieldLabel('I want to…'));
    this.wantSel = document.createElement('select');
    this.wantSel.id = 'compass-want';
    for (const [group, members] of Object.entries(WANT_GROUP)) {
      const og = document.createElement('optgroup');
      og.label = group;
      for (const w of members) {
        const opt = document.createElement('option');
        opt.value = w;
        opt.textContent = humanizeWant(w);
        og.appendChild(opt);
      }
      this.wantSel.appendChild(og);
    }
    this.panel.appendChild(this.wantSel);

    // Fear free-text.
    this.panel.appendChild(this.fieldLabel('I would rather avoid… (optional)'));
    this.fearInput = document.createElement('input');
    this.fearInput.id = 'compass-fear';
    this.fearInput.type = 'text';
    this.fearInput.maxLength = 64;
    this.fearInput.placeholder = 'e.g. being ignored';
    this.panel.appendChild(this.fearInput);

    this.rotateBtn = document.createElement('button');
    this.rotateBtn.id = 'compass-rotate';
    this.rotateBtn.className = 'compass-btn';
    this.rotateBtn.textContent = 'Update Compass';
    this.rotateBtn.addEventListener('click', () => this.rotate());
    this.panel.appendChild(this.rotateBtn);

    // Public opt-in.
    const pubRow = document.createElement('label');
    pubRow.className = 'compass-check';
    this.publicChk = document.createElement('input');
    this.publicChk.type = 'checkbox';
    this.publicChk.id = 'compass-public';
    this.publicChk.addEventListener('change', () => this.setPublic());
    const pubText = document.createElement('span');
    pubText.textContent = 'Show my Compass to nearby strangers';
    pubRow.append(this.publicChk, pubText);
    this.panel.appendChild(pubRow);

    // Per-need visibility opt-ins.
    this.panel.appendChild(this.fieldLabel('Needs others can see'));
    const visWrap = document.createElement('div');
    visWrap.className = 'vis-grid';
    for (const need of NEEDS) {
      const row = document.createElement('label');
      row.className = 'compass-check';
      const chk = document.createElement('input');
      chk.type = 'checkbox';
      chk.addEventListener('change', () => this.setVisibility());
      const txt = document.createElement('span');
      txt.textContent = NEED_META[need].label;
      row.append(chk, txt);
      visWrap.appendChild(row);
      this.visChks.set(need, chk);
    }
    this.panel.appendChild(visWrap);

    // Daily faucet — a gift for showing up, never withheld as a penalty.
    this.rewardBtn = document.createElement('button');
    this.rewardBtn.id = 'compass-reward';
    this.rewardBtn.className = 'compass-btn reward';
    this.rewardBtn.textContent = 'Claim daily Sovereigns';
    this.rewardBtn.addEventListener('click', () => this.claimReward());
    this.panel.appendChild(this.rewardBtn);

    this.noteEl = document.createElement('div');
    this.noteEl.className = 'compass-note';
    this.panel.appendChild(this.noteEl);
  }

  private fieldLabel(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'compass-field-label';
    el.textContent = text;
    return el;
  }

  private toggle(): void {
    this.open ? this.close() : this.show();
  }

  private show(): void {
    this.open = true;
    this.panel.classList.add('show');
    this.renderPanel();
  }

  private close(): void {
    this.open = false;
    this.panel.classList.remove('show');
  }

  /** Reflect the authoritative Compass + Pulse-visibility rows into the controls. */
  private renderPanel(): void {
    if (!this.open) return;
    const me = atlas.identityHex;
    const myCompass = me ? atlas.compasses.get(me) : undefined;
    if (myCompass) {
      if (myCompass.want && WANTS.includes(myCompass.want)) this.wantSel.value = myCompass.want;
      // Only overwrite the Fear field when the user is not editing it.
      if (document.activeElement !== this.fearInput) this.fearInput.value = myCompass.fear;
      this.publicChk.checked = myCompass.public;
    } else {
      this.publicChk.checked = false;
    }
    const p = atlas.localPulse;
    for (const need of NEEDS) {
      const chk = this.visChks.get(need)!;
      chk.checked = p ? this.visOf(p, need) : false;
    }
  }

  private visOf(
    p: NonNullable<typeof atlas.localPulse>,
    need: NeedName,
  ): boolean {
    switch (need) {
      case NEED.Social:
        return p.visSocial;
      case NEED.Fun:
        return p.visFun;
      case NEED.Creativity:
        return p.visCreativity;
      case NEED.Rest:
        return p.visRest;
    }
  }

  private rotate(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const want = this.wantSel.value;
    const fear = this.fearInput.value.trim();
    this.setNote('Updating Compass…');
    void conn.reducers
      .rotateCompass({ want, fear, moodShift: false })
      .then(() => this.setNote('Compass updated.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private setPublic(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const pub = this.publicChk.checked;
    void conn.reducers
      .setCompassPublic({ public: pub })
      .then(() => this.setNote(pub ? 'Compass is now public.' : 'Compass hidden.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private setVisibility(): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .setPulseVisibility({
        visSocial: this.visChks.get(NEED.Social)!.checked,
        visFun: this.visChks.get(NEED.Fun)!.checked,
        visCreativity: this.visChks.get(NEED.Creativity)!.checked,
        visRest: this.visChks.get(NEED.Rest)!.checked,
      })
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private claimReward(): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.setNote('Claiming…');
    void conn.reducers
      .claimDailyPulseReward({})
      .then(() => this.setNote('Daily Sovereigns claimed (or already claimed today).', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private setNote(text: string, cls: '' | 'ok' | 'err' = ''): void {
    this.noteEl.textContent = text;
    this.noteEl.className = `compass-note ${cls}`;
  }

  // ─── Per-frame: Pulse drive + overhead labels ─────────────────────────────────────

  private tick(dt: number): void {
    if (!atlas.hasAccount) return;

    this.pulseDriveAccum += dt;
    if (this.pulseDriveAccum >= PULSE_DRIVE_INTERVAL_S) {
      this.pulseDriveAccum = 0;
      this.driveLocalPulse();
    }

    this.renderLabels();
  }

  /**
   * Project a floating Want/Fear tag over every nearby avatar whose Compass is
   * public. Mirrors ForgeCanvas's cursor projection (behind-camera cull + NDC
   * project). Gold + a Wave button appear only when the stranger's Want is
   * compatible with mine and my own Compass is public.
   */
  private renderLabels(): void {
    let cam: THREE.Camera | null = null;
    try {
      cam = this.app.renderer.cameras.main;
    } catch {
      cam = null;
    }
    const me = atlas.identityHex;
    if (!cam || me === null) {
      this.clearLabels();
      return;
    }

    const myCompass = atlas.compasses.get(me);
    const myWant = myCompass?.want ?? '';
    const myPublic = myCompass?.public ?? false;
    const rangeSq = WAVE_RANGE_M * WAVE_RANGE_M;

    // Common case is NO public compass in range — skip the forced-layout rect
    // read (getBoundingClientRect) until a label actually needs it, and reuse
    // the seen-set across frames (this runs every UI frame).
    if (atlas.remotes.size === 0) {
      this.clearLabels();
      return;
    }
    let rect: DOMRect | null = null;
    cam.getWorldPosition(this.tmpCamPos);
    cam.getWorldDirection(this.tmpCamDir);

    const seen = this._seenScratch;
    seen.clear();
    for (const [hex, r] of atlas.remotes) {
      const compass = atlas.compasses.get(hex);
      // Only legible strangers: a public Compass with a Want, within 10 m.
      if (!compass || !compass.public || !compass.want) continue;
      const dx = r.targetX - atlas.localX;
      const dz = r.targetZ - atlas.localZ;
      if (dx * dx + dz * dz > rangeSq) continue;

      // Absolute row coords → render space (the FP64 rebase shifts the scene
      // root; beyond 10 km every label failed the behind-camera test).
      const sp = (this.app as { world?: { scene?: { position?: { x: number; y: number; z: number } } } } | undefined)?.world?.scene?.position;
      this.tmpWorld.set(r.targetX + (sp?.x ?? 0), 2.1 + (sp?.y ?? 0), r.targetZ + (sp?.z ?? 0));
      this.tmpToPoint.subVectors(this.tmpWorld, this.tmpCamPos);
      if (this.tmpToPoint.dot(this.tmpCamDir) <= 0.05) continue;

      seen.add(hex);
      const compatible = myPublic && wantsCompatible(myWant, compass.want);
      const el = this.labelFor(hex);
      this.tmpProj.copy(this.tmpWorld).project(cam);
      rect ??= this.canvas.getBoundingClientRect(); // lazily, first label only
      const sx = rect.left + (this.tmpProj.x * 0.5 + 0.5) * rect.width;
      const sy = rect.top + (-this.tmpProj.y * 0.5 + 0.5) * rect.height;
      el.style.transform = `translate(${sx}px, ${sy}px) translate(-50%, -120%)`;
      el.classList.toggle('compatible', compatible);

      const wantEl = el.querySelector<HTMLElement>('.sl-want')!;
      wantEl.textContent = humanizeWant(compass.want);
      const fearEl = el.querySelector<HTMLElement>('.sl-fear')!;
      if (compass.fear) {
        fearEl.textContent = `avoids: ${compass.fear}`;
        fearEl.style.display = 'block';
      } else {
        fearEl.style.display = 'none';
      }
      const waveBtn = el.querySelector<HTMLButtonElement>('.sl-wave')!;
      waveBtn.style.display = compatible ? 'inline-block' : 'none';
    }

    for (const [hex, el] of this.labelEls) {
      if (!seen.has(hex)) {
        el.remove();
        this.labelEls.delete(hex);
      }
    }
  }

  /** Lazily create (and cache) the floating label for a stranger. */
  private labelFor(hex: string): HTMLElement {
    let el = this.labelEls.get(hex);
    if (el) return el;
    el = document.createElement('div');
    el.className = 'social-label';
    const who = document.createElement('div');
    who.className = 'sl-who';
    who.textContent = shortHex(hex);
    const want = document.createElement('div');
    want.className = 'sl-want';
    const fear = document.createElement('div');
    fear.className = 'sl-fear';
    const wave = document.createElement('button');
    wave.className = 'sl-wave';
    wave.textContent = '\ud83d\udc4b Wave';
    wave.addEventListener('click', () => this.wave(hex));
    el.append(who, want, fear, wave);
    this.labelsRoot.appendChild(el);
    this.labelEls.set(hex, el);
    return el;
  }

  private clearLabels(): void {
    if (this.labelEls.size === 0) return;
    for (const [, el] of this.labelEls) el.remove();
    this.labelEls.clear();
  }

  private wave(hex: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    let target: Identity;
    try {
      target = Identity.fromString(hex);
    } catch {
      return;
    }
    void conn.reducers
      .sendWave({ target })
      .then(() => { sfx('sfx_wave_greet', { gain: 0.4 }); bus.emit('atlas:status', { text: `\ud83d\udc4b You waved at ${shortHex(hex)}` }); })
      .catch((err: Error) => bus.emit('atlas:status', { text: err.message }));
  }
}
